import React, { useEffect, useMemo, useState } from 'react';
import {
  CalendarDays,
  Clock3,
  CreditCard,
  Package,
  Scissors,
  Store,
  UserRound,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';

import api from '../../lib/api';
import SubpageHeader from './SubpageHeader';

type ReservationDetail = {
  reservation: {
    id: number;
    date: string;
    start_time: string | null;
    end_time: string | null;
    status: string;
    is_free: boolean;
    is_paid: boolean;
    slot: number | null;
    store_id: number;
    store_name: string | null;
    customer_id: number | null;
    customer_name: string | null;
    customer_tel: string | null;
    customer_class: string | null;
    staff_id: number | null;
    staff_nickname: string | null;
  };
  ledger: {
    ledger_id: number;
    payment_method: string;
    service_total: number;
    product_total: number;
    total_amount: number;
    payment_date: string | null;
    created_at: string;
  } | null;
  services: Array<{
    history_id: number;
    service_id: number | null;
    service_name: string | null;
    category_name: string | null;
    date: string | null;
    start_time: string | null;
    end_time: string | null;
    original_price: number | null;
    discount_type: 'none' | 'percent' | 'fixed' | 'coupon' | 'manual';
    discount_value: number;
    final_price: number | null;
  }>;
  products: Array<{
    purchase_id: number;
    product_id: number | null;
    product_name: string | null;
    product_category: string | null;
    date: string | null;
    quantity: number;
    original_unit_price: number | null;
    discount_type: 'none' | 'percent' | 'fixed' | 'coupon' | 'manual';
    discount_value: number;
    final_price: number | null;
  }>;
};

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
});

const ReservationHistoryDetail: React.FC = () => {
  const navigate = useNavigate();
  const { reservationId } = useParams<{ reservationId: string }>();
  const [detail, setDetail] = useState<ReservationDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!reservationId) {
      toast.error('Reservation detail is unavailable');
      navigate('/reserve/history', { replace: true });
      return;
    }

    const loadDetail = async () => {
      try {
        const res = await api.get<ReservationDetail>(`/api/customer-app/reservation/detail/${reservationId}`);
        setDetail(res.data);
      } catch (error) {
        console.error(error);
        toast.error('Failed to load visit detail');
        navigate('/reserve/history', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    void loadDetail();
  }, [navigate, reservationId]);

  const reservation = detail?.reservation ?? null;

  const statusLabel = useMemo(() => {
    if (!reservation) return '';
    if (reservation.status === 'confirmed') return 'Awaiting update';
    if (reservation.status === 'canceled') return 'Canceled';
    return reservation.status.charAt(0).toUpperCase() + reservation.status.slice(1);
  }, [reservation]);

  const statusTone = useMemo(() => {
    if (!reservation) return 'border-slate-200 bg-slate-100 text-slate-700';
    if (reservation.status === 'canceled') return 'border-rose-200 bg-rose-50 text-rose-700';
    if (reservation.status === 'confirmed') return 'border-amber-200 bg-amber-50 text-amber-700';
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }, [reservation]);

  const formatDate = (date: string) =>
    new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    });

  const formatMoney = (value: number | null) => {
    if (value == null) return null;
    return currencyFormatter.format(value);
  };

  const formatPaymentMethod = (value: string) =>
    value
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

  const formatDiscount = (type: string, value: number) => {
    if (type === 'none') return null;
    if (type === 'percent') return `${value}% off`;
    if (type === 'fixed') return `${formatMoney(value) ?? value} off`;
    if (type === 'coupon') return 'Coupon applied';
    if (type === 'manual') return 'Manual discount';
    return `${type} ${value}`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <SubpageHeader title="Visit Detail" onBack={() => navigate('/reserve/history')} />
        <div className="rounded-[28px] border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500 shadow-sm">
          Loading visit detail...
        </div>
      </div>
    );
  }

  if (!detail || !reservation) {
    return null;
  }

  return (
    <div className="space-y-4">
      <SubpageHeader title="Visit Detail" onBack={() => navigate('/reserve/history')} />

      <section className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
        <div className="bg-[linear-gradient(135deg,#0f172a,#1e293b)] px-5 py-5 text-white">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/60">
                Visit Date
              </div>
              <div className="mt-2 text-[26px] font-semibold tracking-[-0.03em]">
                {formatDate(reservation.date)}
              </div>
              <div className="mt-2 text-sm text-white/75">
                {[reservation.start_time, reservation.end_time].filter(Boolean).join(' - ') || 'Time unavailable'}
              </div>
            </div>
            <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${statusTone}`}>
              {statusLabel}
            </span>
          </div>
        </div>

        <div className="grid gap-3 px-5 py-5">
          <div className="flex items-center gap-3 text-sm text-slate-700">
            <Store className="h-4 w-4 text-slate-400" />
            <span>{reservation.store_name || '-'}</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-700">
            <UserRound className="h-4 w-4 text-slate-400" />
            <span>{reservation.staff_nickname || 'Anyone'}</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-700">
            <CalendarDays className="h-4 w-4 text-slate-400" />
            <span>{formatDate(reservation.date)}</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-700">
            <Clock3 className="h-4 w-4 text-slate-400" />
            <span>{[reservation.start_time, reservation.end_time].filter(Boolean).join(' - ') || 'Time unavailable'}</span>
          </div>
        </div>
      </section>

      <section className="rounded-[30px] border border-slate-200 bg-white p-4 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
        <div className="mb-3 flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
          <Scissors className="h-4 w-4 text-slate-400" />
          Services
        </div>

        <div className="space-y-3">
          {detail.services.map((service) => (
            <article
              key={service.history_id}
              className="rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-base font-semibold text-slate-900">
                    {service.service_name || 'Service'}
                  </div>
                  <div className="mt-1 text-sm text-slate-500">
                    {[service.category_name, [service.start_time, service.end_time].filter(Boolean).join(' - ')]
                      .filter(Boolean)
                      .join(' / ') || 'Details unavailable'}
                  </div>
                  {formatDiscount(service.discount_type, service.discount_value) ? (
                    <div className="mt-1 text-xs text-slate-400">
                      {formatDiscount(service.discount_type, service.discount_value)}
                    </div>
                  ) : null}
                </div>
                <div className="text-right">
                  <div className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold text-slate-600">
                    {reservation.is_paid ? 'Finalized' : 'Booked'}
                  </div>
                  {formatMoney(service.final_price) ? (
                    <div className="mt-2 text-sm font-semibold text-slate-900">
                      {formatMoney(service.final_price)}
                    </div>
                  ) : null}
                  {!formatMoney(service.final_price) && formatMoney(service.original_price) ? (
                    <div className="mt-2 text-sm font-semibold text-slate-900">
                      {formatMoney(service.original_price)}
                    </div>
                  ) : null}
                </div>
              </div>
            </article>
          ))}
        </div>

        {!detail.ledger && !reservation.is_paid ? (
          <div className="mt-4 rounded-[22px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Receipt details will appear after the visit is finalized.
          </div>
        ) : null}
      </section>

      {detail.products.length > 0 ? (
        <section className="rounded-[30px] border border-slate-200 bg-white p-4 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
          <div className="mb-3 flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            <Package className="h-4 w-4 text-slate-400" />
            Products
          </div>

          <div className="space-y-3">
            {detail.products.map((product) => (
              <article
                key={product.purchase_id}
                className="rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-base font-semibold text-slate-900">
                      {product.product_name || 'Product'}
                    </div>
                  <div className="mt-1 text-sm text-slate-500">
                      {[product.product_category, `Qty ${product.quantity}`].filter(Boolean).join(' / ')}
                    </div>
                    {formatDiscount(product.discount_type, product.discount_value) ? (
                      <div className="mt-1 text-xs text-slate-400">
                        {formatDiscount(product.discount_type, product.discount_value)}
                      </div>
                    ) : null}
                  </div>
                  {formatMoney(product.final_price ?? product.original_unit_price) ? (
                    <div className="text-sm font-semibold text-slate-900">
                      {formatMoney(product.final_price ?? product.original_unit_price)}
                    </div>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {detail.ledger ? (
        <section className="rounded-[30px] border border-slate-200 bg-white p-4 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
          <div className="mb-3 flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            <CreditCard className="h-4 w-4 text-slate-400" />
            Payment
          </div>

          <div className="space-y-3 rounded-[24px] bg-slate-50 px-4 py-4">
            <div className="flex items-center justify-between gap-3 text-sm text-slate-700">
              <span>Method</span>
              <span className="font-semibold text-slate-900">
                {formatPaymentMethod(detail.ledger.payment_method)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 text-sm text-slate-700">
              <span>Services</span>
              <span className="font-semibold text-slate-900">
                {formatMoney(detail.ledger.service_total)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 text-sm text-slate-700">
              <span>Products</span>
              <span className="font-semibold text-slate-900">
                {formatMoney(detail.ledger.product_total)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-3 text-base text-slate-900">
              <span className="font-semibold">Total</span>
              <span className="font-semibold">
                {formatMoney(detail.ledger.total_amount)}
              </span>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default ReservationHistoryDetail;
